import {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import apiService from "../services/apiService";
import { getLocalStorageItem } from "../utils";

// Define detailed permission types based on API response
export type DetailedPermission = {
  view: string;
  update: string;
  assign: string;
  delete: string;
};

// Define permission types based on your API response
export type UserPermissions = {
  canManageLeads?: boolean;
  canManageRoles?: boolean;
  canManageCustomers?: boolean;
  canManagePartners?: boolean;
  canManageLenders?: boolean;
  canManageTermSheet?: boolean;
  canManageCalculator?: boolean;
  canManageLogin?: boolean;
  canManageSupplier?: boolean;
  canManagePurchaseOrder?: boolean;
  canManageRentalSchedule?: boolean;
  canManageRevenue?: boolean;
  canManageRentalInvoice?: boolean;
  canManagePostDisbursement?: boolean;
  canManagePortfolio?: boolean;
  canManageAdminDashboard?: boolean;

  // Detailed permissions
  detailedPermissions?: {
    manage_lead?: DetailedPermission;
    manage_roles?: DetailedPermission;
    manage_customer?: DetailedPermission;
    manage_partner?: DetailedPermission;
    manage_lender?: DetailedPermission;
    manage_term_sheet?: DetailedPermission;
    manage_calculator?: DetailedPermission;
    manage_login?: DetailedPermission;
    manage_supplier?: DetailedPermission;
    manage_purchase_order?: DetailedPermission;
    manage_rental_schedule?: DetailedPermission;
    manage_revenue?: DetailedPermission;
    manage_rental_invoice?: DetailedPermission;
    manage_post_disbursement?: DetailedPermission;
    manage_portfolio?: DetailedPermission;
    manage_admin_dashboard?: DetailedPermission;
  };
  // Role information
  role?: {
    id: number;
    role_name: string;
    team?: {
      id: number;
      team_name: string;
    };
  };
};

type PermissionContextType = {
  permissions: UserPermissions;
  loading: boolean;
  error: string | null;
};

const PermissionContext = createContext<PermissionContextType>({
  permissions: {},
  loading: true,
  error: null,
});

export const usePermissions = () => useContext(PermissionContext);

export const PermissionProvider = ({ children }: { children: ReactNode }) => {
  const [permissions, setPermissions] = useState<UserPermissions>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        // Get the user ID from localStorage or your auth state management
        const userDetails = getLocalStorageItem("detail");
        const userId = userDetails?.userId;


        if (!userId) {
          console.error("No userId found in localStorage");
          setLoading(false);
          return;
        }

        const response:any = await apiService.get(`/users/${userId}`);


        // Check where the permissions data is located in the response
        if (response?.role) {

          // Extract the detailed permissions directly from the response
          const detailedPermissions = response?.role?.permissions || {};

          // Calculate the existing boolean permissions as before
          const hasPermissionToManageLead = Object.values(
            response?.role?.permissions?.manage_lead || {}
          )?.some((action) => action !== "");

          const hasPermissionToManageRole = Object.values(
            response?.role?.permissions?.manage_roles || {}
          )?.some((action) => action !== "");

          const hasPermissionToManageCustomer = Object.values(
            response?.role?.permissions?.manage_customer || {}
          )?.some((action) => action !== "");

          const hasPermissionToManagePartner = Object.values(
            response?.role?.permissions?.manage_partner || {}
          )?.some((action) => action !== "");

          const hasPermissionToManageLender = Object.values(
            response?.role?.permissions?.manage_lender || {}
          )?.some((action) => action !== "");

          const hasPermissionToManageTermSheet = Object.values(
            response?.role?.permissions?.manage_term_sheet || {}
          )?.some((action) => action !== "");

          const hasPermissionToManageCalculator = Object.values(
            response?.role?.permissions?.manage_calculator || {}
          )?.some((action) => action !== "");

          const hasPermissionToManageLogin = Object.values(
            response?.role?.permissions?.manage_login || {}
          )?.some((action) => action !== "");

          const hasPermissionToManageSupplier = Object.values(
            response?.role?.permissions?.manage_supplier || {}
          )?.some((action) => action !== "");

          const hasPermissionToManagePurchaseOrder = Object.values(
            response?.role?.permissions?.manage_purchase_order || {}
          )?.some((action) => action !== "");

          const hasPermissionToManageRentalSchedule = Object.values(
            response?.role?.permissions?.manage_rental_schedule || {}
          )?.some((action) => action !== "");

          const hasPermissionToManageRevenue = Object.values(
            response?.role?.permissions?.manage_revenue || {}
          )?.some((action) => action !== "");

          const hasPermissionToManageRentalInvoice = Object.values(
            response?.role?.permissions?.manage_rental_invoice || {}
          )?.some((action) => action !== "");

          const hasPermissionToManagePostDisbursement = Object.values(
            response?.role?.permissions?.manage_post_disbursement || {}
          )?.some((action) => action !== "");


          const hasPermissionToManagePortfolio = Object.values(
            response?.role?.permissions?.manage_portfolio || {}
          )?.some((action) => action !== "");

          const hasPermissionToManageAdminDashboard = Object.values(
            response?.role?.permissions?.manage_admin_dashboard || {}
          )?.some((action) => action !== "");

          // Combine both permission structures
          const userPermissions: UserPermissions = {
            // Keep the existing boolean flags
            canManageLeads: Boolean(hasPermissionToManageLead),
            canManageRoles: Boolean(hasPermissionToManageRole),
            canManageCustomers: Boolean(hasPermissionToManageCustomer),
            canManagePartners: Boolean(hasPermissionToManagePartner),
            canManageLenders: Boolean(hasPermissionToManageLender),
            canManageTermSheet: Boolean(hasPermissionToManageTermSheet),
            canManageCalculator: Boolean(hasPermissionToManageCalculator),
            canManageLogin: Boolean(hasPermissionToManageLogin),
            canManageSupplier: Boolean(hasPermissionToManageSupplier),
            canManagePurchaseOrder: Boolean(hasPermissionToManagePurchaseOrder),
            canManageRentalSchedule: Boolean(hasPermissionToManageRentalSchedule),
            canManageRevenue: Boolean(hasPermissionToManageRevenue),
            canManageRentalInvoice: Boolean(hasPermissionToManageRentalInvoice),
            canManagePostDisbursement: Boolean(hasPermissionToManagePostDisbursement),
            canManagePortfolio: Boolean(hasPermissionToManagePortfolio),
            canManageAdminDashboard: Boolean(hasPermissionToManageAdminDashboard),
            // Add the detailed permissions
            detailedPermissions: detailedPermissions,
            // Add the role information
            role: {
              id: response?.role?.id,
              role_name: response?.role?.role_name,
              team: response?.role?.team
                ? {
                  id: response?.role?.team?.id,
                  team_name: response?.role?.team?.team_name,
                }
                : undefined,
            },
          };

          setPermissions(userPermissions);
        } else {
          console.error("Couldn't find permissions data in response");
          // Set default permissions to prevent errors
          setPermissions({
            canManageLeads: false,
            canManageRoles: false,
            canManageCustomers: false,
            canManagePartners: false,
            canManageLenders: false,
            canManageTermSheet: false,
            canManageCalculator: false,
            canManageLogin: false,
            canManageSupplier: false,
            canManagePurchaseOrder: false,
            canManageRentalSchedule: false,
            canManageRevenue: false,
            canManageRentalInvoice: false,
            canManagePostDisbursement: false,
            canManagePortfolio: false,
            canManageAdminDashboard: false,
            detailedPermissions: {},
          });
        }
      } catch (err) {
        console.error("Failed to fetch user permissions:", err);
        setError("Failed to load permissions");
        // Set default permissions to prevent errors
        setPermissions({
          canManageLeads: false,
          canManageRoles: false,
          canManageCustomers: false,
          canManagePartners: false,
          canManageLenders: false,
          canManageTermSheet: false,
          canManageCalculator: false,
          canManageLogin: false,
          canManageSupplier: false,
          canManagePurchaseOrder: false,
          canManageRentalSchedule: false,
          canManageRevenue: false,
          canManageRentalInvoice: false,
          canManagePostDisbursement: false,
          canManagePortfolio: false,
          canManageAdminDashboard: false,
          detailedPermissions: {},
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, []);

  return (
    <PermissionContext.Provider value={{ permissions, loading, error }}>
      {children}
    </PermissionContext.Provider>
  );
};
